import type {
  ApiResponse,
  CloudFolder,
  CloudProvider,
  Playlist,
  BroadcastConfig,
  PublicBroadcastState,
  SavedPlaylist,
  StreamUrlResponse,
  UserSettings,
  TokenState,
  SortOrder,
  PlaylistScanStatus,
  RoomProfile,
  BumperRule,
  VideoRating,
} from '../types'

export class ApiError extends Error {
  constructor(message: string, readonly status: number) { super(message); this.name = 'ApiError' }
}

const BASE_URL = '/api'

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  })

  const data: ApiResponse<T> = await res.json()

  if (!res.ok) {
    throw new ApiError(data.error ?? `Request failed: ${res.status}`, res.status)
  }

  return data.data as T
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const authApi = {
  getStatus(): Promise<{ viewer: { email: string; isOwner: boolean; canSavePlayback: boolean }; google: TokenState; onedrive: TokenState }> {
    return request('/auth/status')
  },

  getGoogleAuthUrl(): Promise<{ url: string }> {
    return request('/auth/google/url')
  },

  getOneDriveAuthUrl(): Promise<{ url: string }> {
    return request('/auth/onedrive/url')
  },

  disconnect(provider: CloudProvider): Promise<void> {
    return request(`/auth/${provider}/disconnect`, { method: 'POST' })
  },

  refreshToken(provider: CloudProvider): Promise<void> {
    return request(`/auth/${provider}/refresh`, { method: 'POST' })
  },
}

export interface GuestAccess {
  email: string
  roomId: string
  expiresAt: string
  createdAt: string
  active: boolean
}

export type GuestDuration = '24h' | '1w' | '1m' | '3m'

export const guestApi = {
  list(): Promise<GuestAccess[]> {
    return request('/guests')
  },

  create(email: string, duration: GuestDuration = '24h'): Promise<GuestAccess> {
    return request('/guests', {
      method: 'POST',
      body: JSON.stringify({ email, duration }),
    })
  },

  revoke(email: string): Promise<void> {
    return request(`/guests/${encodeURIComponent(email)}`, { method: 'DELETE' })
  },
}

export const diagnosticsApi = {
  logPlayback(event: {
    level: 'info' | 'warn' | 'error'
    eventName: 'playback_started' | 'playback_buffering' | 'playback_error' | 'stream_url_failed' | 'playback_recovered'
    videoId: string | null
    details?: Record<string, unknown>
  }): Promise<void> {
    return request('/diagnostics/playback', { method: 'POST', body: JSON.stringify(event) })
  },
}

// ─── Broadcast Output ────────────────────────────────────────────────────────

export const broadcastApi = {
  getConfig(): Promise<BroadcastConfig> {
    return request('/broadcast')
  },

  rotate(enabled = true): Promise<BroadcastConfig> {
    return request('/broadcast', {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    })
  },

  publishState(state: {
    videoId: string | null
    isPlaying: boolean
    position: number
    observedAt: number
    volume: number
    muted: boolean
  }): Promise<{ revision: number }> {
    return request('/broadcast/state', { method: 'PUT', body: JSON.stringify(state) })
  },
}

export const publicBroadcastApi = {
  async getState(token: string): Promise<PublicBroadcastState> {
    const res = await fetch(`/api/public/broadcast/${encodeURIComponent(token)}/state`)
    const data: ApiResponse<PublicBroadcastState> = await res.json()
    if (!res.ok) throw new ApiError(data.error ?? `Request failed: ${res.status}`, res.status)
    return data.data as PublicBroadcastState
  },
}

// ─── Folders ──────────────────────────────────────────────────────────────────

export const folderApi = {
  listFolders(provider: CloudProvider, parentId?: string): Promise<CloudFolder[]> {
    const params = parentId ? `?parentId=${encodeURIComponent(parentId)}` : ''
    return request(`/folders/${provider}${params}`)
  },

  selectFolder(provider: CloudProvider, folderId: string, folderName: string, folderPath: string): Promise<void> {
    return request('/folders/select', {
      method: 'POST',
      body: JSON.stringify({ provider, folderId, folderName, folderPath }),
    })
  },
}

export const roomApi = {
  list(): Promise<RoomProfile[]> {
    return request('/rooms')
  },

  create(name: string): Promise<RoomProfile> {
    return request('/rooms', { method: 'POST', body: JSON.stringify({ name }) })
  },

  select(roomId: string): Promise<{ roomId: string }> {
    return request(`/rooms/${encodeURIComponent(roomId)}/select`, { method: 'POST' })
  },

  update(roomId: string, patch: { name?: string; defaultPlaylistId?: string | null }): Promise<RoomProfile> {
    return request(`/rooms/${encodeURIComponent(roomId)}`, {
      method: 'PATCH', body: JSON.stringify(patch),
    })
  },
}

// ─── Playlist ─────────────────────────────────────────────────────────────────

export const playlistApi = {
  getPlaylist(sortOrder: SortOrder, signal?: AbortSignal): Promise<Playlist> {
    return request(`/playlist?sort=${sortOrder}`, { signal })
  },

  scanFolder(): Promise<{ jobId: string }> {
    return request('/playlist/scan', { method: 'POST' })
  },

  getScanStatus(jobId: string): Promise<PlaylistScanStatus> {
    return request(`/playlist/scan/${jobId}`)
  },
}

// ─── Saved Playlists ─────────────────────────────────────────────────────────

export const savedPlaylistApi = {
  list(): Promise<SavedPlaylist[]> {
    return request('/saved-playlists')
  },

  create(name: string, videoIds: string[]): Promise<SavedPlaylist> {
    return request('/saved-playlists', {
      method: 'POST',
      body: JSON.stringify({ name, videoIds }),
    })
  },

  delete(playlistId: string): Promise<void> {
    return request(`/saved-playlists/${encodeURIComponent(playlistId)}`, {
      method: 'DELETE',
    })
  },

  saveResume(playlistId: string, state: {
    videoId: string | null
    position: number
    queueIds: string[]
    completed: boolean
    revision: number
  }): Promise<SavedPlaylist> {
    return request(`/saved-playlists/${encodeURIComponent(playlistId)}/resume`, {
      method: 'PUT', body: JSON.stringify(state),
    })
  },

  saveBumperRule(playlistId: string, rule: BumperRule | null): Promise<SavedPlaylist> {
    return request(`/saved-playlists/${encodeURIComponent(playlistId)}/bumper-rule`, {
      method: 'PUT', body: JSON.stringify({ rule }),
    })
  },
}

// ─── Video Streaming ──────────────────────────────────────────────────────────

export const videoApi = {
  getStreamUrl(videoId: string, provider: CloudProvider): Promise<StreamUrlResponse> {
    return request(`/video/${provider}/${encodeURIComponent(videoId)}/stream`)
  },

  recordPlay(videoId: string, provider: CloudProvider, eventId: string, roomId: string | null): Promise<void> {
    return request(`/video/${provider}/${encodeURIComponent(videoId)}/play`, { method: 'POST', body: JSON.stringify({ eventId, roomId }) })
  },

  hideFromRoom(videoId: string, provider: CloudProvider): Promise<{ hidden: boolean; sourceDeleted: boolean }> {
    return request(`/video/${provider}/${encodeURIComponent(videoId)}/hide`, { method: 'POST' })
  },

  reportError(videoId: string, error: string): Promise<void> {
    return request('/video/error', {
      method: 'POST',
      body: JSON.stringify({ videoId, error }),
    })
  },
}

export const ratingApi = {
  list(): Promise<VideoRating[]> {
    return request('/ratings')
  },

  update(videoId: string, provider: CloudProvider, patch: { rating?: number; liked?: boolean }): Promise<VideoRating> {
    return request(`/ratings/${provider}/${encodeURIComponent(videoId)}`, {
      method: 'PUT', body: JSON.stringify(patch),
    })
  },
}

// ─── Settings & State ─────────────────────────────────────────────────────────

export const settingsApi = {
  getSettings(): Promise<UserSettings> {
    return request('/settings')
  },

  updateSettings(settings: Partial<UserSettings>): Promise<UserSettings> {
    return request('/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    })
  },

  savePlaybackState(videoId: string, position: number): Promise<void> {
    return request('/settings/playback', {
      method: 'POST',
      body: JSON.stringify({ videoId, position, timestamp: Date.now() }),
    })
  },
}

export interface ManagedAccount {
  email: string
  expiresAt: string
  role: 'viewer' | 'controller'
  suspended: number
  plays: number
  lastWatched: string | null
}
export interface AccountActivity {
  events: Array<{ id: string; email: string; title: string; room_id: string; location: string; device: string; created_at: string }>
  summary: Array<{ email: string; plays: number; titles: number; lastWatched: string }>
  auditLog: Array<{ id: string; actor: string; target: string; action: string; created_at: string }>
}
export const accountsApi = {
  list: (): Promise<ManagedAccount[]> => request('/accounts'),
  update: (email: string, changes: { role: ManagedAccount['role']; suspended: boolean } | { newEmail: string }): Promise<{ ok: true; warning?: string }> =>
    request('/accounts', { method: 'PATCH', body: JSON.stringify({ email, ...changes }) }),
  activity: (email: string, days: number): Promise<AccountActivity> =>
    request(`/accounts/activity?email=${encodeURIComponent(email)}&days=${days}`),
}
